import { map, tap, Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';

import { FiltersMap } from '@/app/core/services/http/types/http-filters';

import {
  ItemResponse,
  ListResponse,
  PayloadResponse,
  FiltersResponse,
  PaginationResponse,
} from './types/http-response';
import {
  HttpGetConfig,
  HttpPutConfig,
  HttpPostConfig,
  HttpDeleteConfig,
  HttpDownloadConfig,
  HttpDownloadResult,
} from './types/http-core';

import { saveFile } from '@/app/shared/utils/save-file';
import { environment } from '@/environments/environment';
import { buildParamsStrict } from '@/app/shared/utils/build-params-strict';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  readonly baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.apiUrl;
  }

  private _url(path: string) {
    return `${this.baseUrl}${path}`;
  }

  getRaw<T>(path: string, config?: HttpGetConfig) {
    return this.httpClient.get<ItemResponse<T>>(this._url(path), config);
  }

  get<T>(path: string, config?: HttpGetConfig): Observable<T> {
    return this.httpClient.get<ItemResponse<T>>(this._url(path), config).pipe(map((res) => res.payload.content));
  }

  getPaginatedList<T>(path: string, config?: HttpGetConfig): Observable<PayloadResponse<T[], PaginationResponse>> {
    let params: HttpParams | undefined = undefined;

    if (config?.params) {
      params = config.params instanceof HttpParams ? config.params : buildParamsStrict(config.params);
    }

    return this.httpClient
      .get<ListResponse<T>>(this._url(path), {
        ...config,
        params,
      })
      .pipe(map((res) => res.payload));
  }

  getList<T>(path: string, config?: HttpGetConfig): Observable<T[]> {
    return this.getPaginatedList<T>(path, config).pipe(map((res) => res.content));
  }

  post<T, P = unknown>(path: string, body: P | null, config?: HttpPostConfig): Observable<T> {
    return this.httpClient.post<ItemResponse<T>>(this._url(path), body, config).pipe(map((res) => res.payload.content));
  }

  put<T, P = unknown>(path: string, body: P, config?: HttpPutConfig): Observable<T> {
    return this.httpClient.put<ItemResponse<T>>(this._url(path), body, config).pipe(map((res) => res.payload.content));
  }

  delete<T = null>(path: string, config?: HttpDeleteConfig): Observable<T> {
    return this.httpClient.delete<ItemResponse<T>>(this._url(path), config).pipe(map((res) => res.payload.content));
  }

  getFilters<TFilters extends FiltersMap = FiltersMap>(path: string, config?: HttpGetConfig) {
    let params: HttpParams | undefined = undefined;

    if (config?.params) {
      params = config.params instanceof HttpParams ? config.params : buildParamsStrict(config.params);
    }

    return this.httpClient
      .get<FiltersResponse<TFilters>>(this._url(path), {
        ...config,
        params,
      })
      .pipe(map((res) => res.payload.content));
  }

  downloadFile(path: string, config?: HttpDownloadConfig) {
    const method = config?.method || 'GET';

    let params: HttpParams | undefined = undefined;

    if (config?.params) {
      params = config.params instanceof HttpParams ? config.params : buildParamsStrict(config.params);
    }

    return this.httpClient
      .request(method, this._url(path), {
        observe: 'response',
        responseType: 'blob',
        ...config,
        params,
      })
      .pipe(
        map<HttpResponse<Blob>, HttpDownloadResult>((response) => ({
          blob: response.body!,
          filename: config?.filename || this.extractFilename(response),
          contentType: response.headers.get('Content-Type'),
          response,
        })),
      );
  }

  downloadAndSave(path: string, config?: HttpDownloadConfig) {
    return this.downloadFile(path, config).pipe(
      tap(({ blob, filename: detectedFilename }) => {
        saveFile(blob, config?.filename || detectedFilename || 'download');
      }),
      map(() => void 0),
    );
  }

  private extractFilename(response: HttpResponse<Blob>): string | null {
    const contentDisposition =
      response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]?)?([^;\r\n"']*)['"]?;?/i);
      if (filenameMatch && filenameMatch[1]) {
        return decodeURIComponent(filenameMatch[1]);
      }
    }
    return null;
  }
}
